
import { useState, useEffect } from 'react';
import { DoctorScore, ClinicScore } from '@/types/scoring';
import { calculateDoctorScore, calculateClinicScore } from '@/utils/scoreCalculation';

// Mock data for doctor score
const MOCK_DOCTOR_METRICS = {
  'doctor-1': {
    responseTimeToPatients: 85,
    planAcceptanceRate: 78,
    consultationAttendance: 92,
    treatmentReportsUploaded: 65,
    paymentCompliance: 100,
    adEngagement: 72
  }
};

const MOCK_DOCTOR_PREVIOUS_SCORES = {
  'doctor-1': [
    { date: '2025-03-12', score: 76 },
    { date: '2025-03-19', score: 78 },
    { date: '2025-03-26', score: 77 },
    { date: '2025-04-02', score: 75 },
    { date: '2025-04-09', score: 81 }
  ]
};

// Mock data for clinic score
const MOCK_CLINIC_METRICS = {
  'clinic-1': {
    reportSubmissions: 72,
    paymentTimeliness: 95,
    adEngagement: 68,
    doctorActivationRate: 82,
    responsivenessToAdmin: 90
  }
};

const MOCK_CLINIC_PREVIOUS_SCORES = {
  'clinic-1': [
    { date: '2025-03-12', score: 81 },
    { date: '2025-03-19', score: 80 },
    { date: '2025-03-26', score: 82 },
    { date: '2025-04-02', score: 79 },
    { date: '2025-04-09', score: 83 }
  ]
};

export const useDoctorScore = (doctorId: string) => {
  const [score, setScore] = useState<DoctorScore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // In a real app, this would be an API call
    const fetchScore = async () => {
      try {
        setIsLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get mock metrics for this doctor
        const metrics = MOCK_DOCTOR_METRICS[doctorId as keyof typeof MOCK_DOCTOR_METRICS];
        const previousScores = MOCK_DOCTOR_PREVIOUS_SCORES[doctorId as keyof typeof MOCK_DOCTOR_PREVIOUS_SCORES];
        
        if (!metrics) {
          throw new Error(`No metrics found for doctor ID: ${doctorId}`);
        }
        
        // Calculate score
        const doctorScore = calculateDoctorScore(
          doctorId,
          'Dr. John Smith', // In a real app, get the actual name
          metrics,
          previousScores
        );
        
        setScore(doctorScore);
      } catch (err) {
        console.error('Error fetching doctor score:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchScore();
  }, [doctorId]);

  return { score, isLoading, error };
};

export const useClinicScore = (clinicId: string) => {
  const [score, setScore] = useState<ClinicScore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // In a real app, this would be an API call
    const fetchScore = async () => {
      try {
        setIsLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get mock metrics for this clinic
        const metrics = MOCK_CLINIC_METRICS[clinicId as keyof typeof MOCK_CLINIC_METRICS];
        const previousScores = MOCK_CLINIC_PREVIOUS_SCORES[clinicId as keyof typeof MOCK_CLINIC_PREVIOUS_SCORES];
        
        if (!metrics) {
          throw new Error(`No metrics found for clinic ID: ${clinicId}`);
        }
        
        // Calculate score
        const clinicScore = calculateClinicScore(
          clinicId,
          'Dental Excellence Clinic', // In a real app, get the actual name
          metrics,
          previousScores
        );
        
        setScore(clinicScore);
      } catch (err) {
        console.error('Error fetching clinic score:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchScore();
  }, [clinicId]);

  return { score, isLoading, error };
};
